import { PageEditMode } from './page-edit-mode';
import { PageEditModeStatus } from './page-edit-mode-status';
import { ApplicationStateService } from '../../services/application-state.service';
import { Page } from '../../models/page';
import { TextSelection } from '../../models/text-selection';
import { Position } from '../../models/position';
import { TextBox } from '../../models/text-box';
import { PageTextService } from '../../services/page-text.service';
import { PageComponent } from '../../components/bookviewer/page/page.component';

export class SelectionPageEditMode implements PageEditMode {

    status: PageEditModeStatus;
    type = 'SELECTION';

    private applicationStateService: ApplicationStateService;
    private pageTextService: PageTextService;
    private page: Page;
    private textBoxes: Array<TextBox>;
    private textSelection: TextSelection;

    private maxIndex = -1;
    private minIndex: number = Number.MAX_VALUE;
    private minBox: TextBox;
    private initialized: boolean;

    constructor(applicationStateService: ApplicationStateService,
                pageTextService: PageTextService) {

        this.applicationStateService = applicationStateService;
        this.pageTextService = pageTextService;
        this.textSelection = this.createEmptyTextSelection();
        this.status = PageEditModeStatus['INIT'];
        this.minBox = TextBox.createEmptyTextBox();
        this.minBox.index = Number.MAX_VALUE;
    }

    static getClassName(): string {
        return new SelectionPageEditMode(
            undefined,
            undefined).constructor.name;
    }

    /**
     * Set page
     *
     * @param {PageComponent} pageComponent
     */
    setPage(pageComponent: PageComponent): void {
        this.page = pageComponent.page;
        this.textBoxes = pageComponent.textBoxes;
    }

    /**
     * Handler for mousedown/touchstart event.
     * - Handles initialization of new text selection
     *
     * @param {Event} $event
     */
    onMouseDown($event): void {
        if (!this.applicationStateService.isPageEditable) {
            return;
        }

        if (this.status !== PageEditModeStatus['INIT'] && this.initialized) {
            this.onMouseUp($event);

            return;
        }

        const textBox: TextBox = this.getTextBox($event);
        if (!textBox) {
            this.status = PageEditModeStatus['STARTED'];

            return;
        }

        this.textSelection.startBox = TextBox.createEmptyTextBox();
        this.textSelection.startBox.index = Number.MAX_VALUE;
        this.textSelection.startBox.pageId = this.page.id;
        this.textSelection.endBox = TextBox.createEmptyTextBox();
        this.textSelection.endBox.index = -1;
        this.textSelection.endBox.pageId = this.page.id;
        this.textSelection.pageId = this.page.id;
        this.textSelection.index = this.page.index;

        if (textBox) {
            textBox.isHighlighted = true;
            this.textSelection.startBox = textBox;
            this.minBox = textBox;
        }

        this.textSelection.pageId = this.page.id;
        this.initialized = true;
        this.status = PageEditModeStatus['STARTED'];
    }

    /**
     * Handler for mousemove/touchmove event.
     * - Handles highlighting of text boxes for selection.
     *
     * @param {Event} $event
     */
    onMouseMove($event): void {

        if (!this.applicationStateService.isPageEditable
            || this.status === PageEditModeStatus['INIT']
            || this.status === PageEditModeStatus['FINISHED']
            || !this.textSelection.startBox
        ) {
            return;
        }

        const textBox: TextBox = this.getTextBox($event);
        if (!textBox) {
            return;
        }

        if (!this.initialized) {
            this.onMouseDown($event);
        }

        this.status = PageEditModeStatus['PENDING'];

        if (!this.minBox || this.minBox.index === Number.MAX_VALUE) {
            this.minBox = textBox;
        }

        if (textBox.index < this.minBox.index) {
            this.textSelection.startBox = textBox;
            this.textSelection.endBox = this.minBox;
        } else {
            this.textSelection.startBox = this.minBox;
            this.textSelection.endBox = textBox;
        }

        if (this.textBoxes) {
            this.textBoxes.slice(this.textSelection.startBox.index, this.textSelection.endBox.index + 1).forEach(box => {
                box.isHighlighted = true;
            });
        }

        if (this.textSelection.endBox.index > this.maxIndex) {
            this.maxIndex = this.textSelection.endBox.index;
        }

        if (this.textSelection.startBox.index < this.minIndex) {
            this.minIndex = this.textSelection.startBox.index;
        }

        if (this.textSelection.endBox.index < this.maxIndex) {
            this.textBoxes.slice(this.textSelection.endBox.index, this.maxIndex + 1).forEach(box => {
                box.isHighlighted = false;
            });
        }

        if (this.textSelection.startBox.index > this.minIndex) {
            this.textBoxes.slice(this.minIndex, this.textSelection.startBox.index).forEach(box => {
                box.isHighlighted = false;
            });
        }

        textBox.isHighlighted = true;
    }

    /**
     * Handler for mouseup/touchend event.
     * - Handles saving of a text selection.
     *
     * @param {Event} $event
     */
    onMouseUp($event): void {

        if (!this.applicationStateService.isPageEditable
            || !this.textSelection.startBox
            || !this.textSelection.pageId
            || this.status !== PageEditModeStatus['PENDING']
            || this.textBoxes.length === 0
        ) {
            return;
        }

        this.pageTextService.highlightTextBoxesOfSelection(this.textSelection, this.textBoxes, this.page.id);
        this.pageTextService.createTextSelection(this.textSelection).then(() => {
            this.status = PageEditModeStatus['FINISHED'];
            this.maxIndex = -1;
            this.minIndex = Number.MAX_VALUE;
        });
    }

    onMouseLeave($event): void {
        if (!this.applicationStateService.isPageEditable || !this.textSelection.startBox) {
            return;
        }
    }

    onPinch($event): void {
        return;
    }

    getResult(): TextSelection {
        return this.textSelection;
    }

    getStatus(): PageEditModeStatus {
        return this.status;
    }

    getPage(): Page {
        return this.page;
    }

    /**
     * Get the textbox the cursor currently hovers.
     *
     * @param {Event} $event
     */
    private getTextBox($event): any {
        const moveLocation = $event;

        let $realTarget;
        if (moveLocation.targetTouches) {
            $realTarget = $(document.elementFromPoint(moveLocation.targetTouches[0].pageX, moveLocation.targetTouches[0].pageY));
        } else {
            $realTarget = $(document.elementFromPoint(moveLocation.clientX, moveLocation.clientY));
        }

        if ($realTarget.length <= 0) {
            return;
        }

        const boxIndex = $realTarget.data('index');
        if (boxIndex === null || boxIndex === undefined) {
            return;
        }

        return this.pageTextService.getTextBoxByPageAndIndex(this.page, boxIndex);
    }

    private createEmptyTextSelection(): TextSelection {
        const textSelection = new TextSelection();
        textSelection.position = Position.createEmptyPosition();
        textSelection.startBox = TextBox.createEmptyTextBox();
        textSelection.endBox = TextBox.createEmptyTextBox();

        return textSelection;
    }

}
